/**
 * Unsubscribe / "Not me" handler page.
 *
 * /confirm/:token/not-me  → action = 'not_me'  (wrong boat association)
 * /unsubscribe/:token     → action = 'unsubscribe'  (global opt-out)
 *
 * Shows a confirmation page first; the POST to
 * /api/v4/invitations/:token/unsubscribe only fires on an explicit button
 * click. Never unsubscribe on page load: corporate mail scanners
 * (SafeLinks, Proofpoint, …) prefetch links in email, and an on-load POST
 * would suppress recipients before they ever read the message.
 * No auth required — bearer-token is the invitation token in the URL.
 */

import { useState } from 'react';
import { useParams } from 'react-router-dom';
import { api } from '@/lib/api';

type Props = {
  /** 'not_me' = this isn't me; 'unsubscribe' = please stop emailing me */
  action: 'not_me' | 'unsubscribe';
};

type PageState = 'confirm' | 'submitting' | 'done' | 'error';

export function UnsubscribePage({ action }: Props) {
  const { token } = useParams<{ token: string }>();
  const [state, setState] = useState<PageState>(token ? 'confirm' : 'error');

  async function handleConfirm() {
    if (!token) return;
    setState('submitting');
    try {
      await api.post(`/invitations/${token}/unsubscribe`, { action });
      setState('done');
    } catch {
      setState('error');
    }
  }

  const headings = {
    not_me: {
      confirm: 'Not your boat?',
      confirmBody:
        "Confirm below and we'll record that this entry isn't yours and stop contacting you about it.",
      confirmCta: "This isn't me",
      done: 'Got it — noted',
      doneBody:
        "We've recorded that this entry isn't yours and won't contact you about it again. If you believe you received this in error, no further action is needed.",
      error: "Couldn't process your request",
    },
    unsubscribe: {
      confirm: 'Unsubscribe from regatta emails?',
      confirmBody:
        "Confirm below and we'll remove your address from our pre-regatta notification list.",
      confirmCta: 'Unsubscribe',
      done: "You've been unsubscribed",
      doneBody:
        "We've removed your address from our pre-regatta notification list. If you change your mind, you can re-register directly at regattareplay.com.",
      error: "Couldn't process your request",
    },
  };

  const h = headings[action];

  return (
    <div className="flex items-center justify-center px-4 py-16">
      <div className="w-full max-w-md">
        {(state === 'confirm' || state === 'submitting') && (
          <div className="rounded-lg border border-gray-200 bg-white p-8 shadow-sm text-center" data-testid="unsubscribe-confirm">
            <h1 className="text-xl font-semibold text-navy-900">{h.confirm}</h1>
            <p className="mt-3 text-sm text-muted-foreground">{h.confirmBody}</p>
            <button
              type="button"
              onClick={handleConfirm}
              disabled={state === 'submitting'}
              className="mt-6 inline-flex min-h-[44px] items-center justify-center rounded-md bg-navy-900 px-6 text-sm font-semibold text-white transition-colors hover:bg-navy-700 disabled:opacity-60"
            >
              {state === 'submitting' ? 'Processing…' : h.confirmCta}
            </button>
          </div>
        )}

        {state === 'done' && (
          <div className="rounded-lg border border-gray-200 bg-white p-8 shadow-sm text-center" data-testid="unsubscribe-done">
            <div className="mx-auto mb-4 flex h-12 w-12 items-center justify-center rounded-full bg-green-100">
              <svg className="h-6 w-6 text-green-600" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                <path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" />
              </svg>
            </div>
            <h1 className="text-xl font-semibold text-navy-900">{h.done}</h1>
            <p className="mt-3 text-sm text-muted-foreground">{h.doneBody}</p>
          </div>
        )}

        {state === 'error' && (
          <div className="rounded-lg border border-gray-200 bg-white p-8 shadow-sm text-center" data-testid="unsubscribe-error">
            <div className="mx-auto mb-4 flex h-12 w-12 items-center justify-center rounded-full bg-red-100">
              <svg className="h-6 w-6 text-red-600" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                <path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" />
              </svg>
            </div>
            <h1 className="text-xl font-semibold text-navy-900">{h.error}</h1>
            <p className="mt-2 text-sm text-muted-foreground">
              The link may have already been used or may have expired. If the issue persists,
              please contact us at <a href="mailto:hello@regattareplay.com" className="underline">hello@regattareplay.com</a>.
            </p>
          </div>
        )}
      </div>
    </div>
  );
}
